import { Injectable, inject } from '@angular/core';
import { BooleanString } from '@shared/enums/boolean-string';
import { buildQueryFilter } from '@shared/helpers/build-query-filter';
import { Mapper } from '@shared/helpers/mapper';
import {
  Client,
  Country,
  Entity,
  EntityType,
  InvestmentJurisdiction,
  ResidencyJurisdiction,
} from '@shared/models/entity';
import {
  Question,
  SecurityQuestionAnswer,
  SecurityReview,
  SecurityType,
} from '@shared/models/security';
import { Security } from '@shared/models/security/security';
import { SecurityReviewResult } from '@shared/models/security/security-review-result';
import { SecurityStatus } from '@shared/models/security/security-status';
import { SecurityTaxResultItem } from '@shared/models/security/security-tax-result-item';
import { TaxPolicy } from '@shared/models/security/tax-policy';
import { TaxResultItemType } from '@shared/models/security/tax-result-item-type';
import { TransactionType } from '@shared/models/security/transaction-type';
import { HeaderService } from '@shared/services';
import { QueryOptions } from 'odata-query';
import { Observable, map } from 'rxjs';
import {
  ApproveSecurityRequest,
  GstarClient,
  SecurityActionResult,
  SecurityByIdResponseWithNestedObjectsAuditable,
  SecurityQuestionAnswerAuditableResponse,
  SecurityResponseWithNestedObjectsAuditable,
  SecurityResponseWithNestedObjectsAuditableODataWithMetadataResponse,
  SecurityReviewResponseWithAnswersAndNestedObjects,
  SecurityReviewResponseWithAnswersAndNestedObjectsODataWithMetadataResponse,
  SecurityTaxResultItemNestedObjectResponse,
  TaxPolicyForSecurityReviewResponse,
  UpdateSecurityRequest,
  UpdateSecurityTransactionTypeRequest,
} from 'src/app/web-api-client';

@Injectable({
  providedIn: 'root',
})
export class SecurityService {
  private readonly gstarClient = inject(GstarClient);
  private readonly headerService = inject(HeaderService);

  query(query?: Partial<QueryOptions<Security>>): Observable<Security[]> {
    return this.gstarClient
      .byclient3(
        undefined,
        query?.select?.toString(),
        buildQueryFilter(query),
        query?.top?.toString(),
        query?.skip?.toString(),
        query?.orderBy?.toString(),
        query?.count?.toString(),
        undefined
      )
      .pipe(
        map(
          (
            response: SecurityResponseWithNestedObjectsAuditableODataWithMetadataResponse
          ) => {
            if (response.value && response.value.length > 0) {
              return response.value.map(
                (security: SecurityResponseWithNestedObjectsAuditable) =>
                  this.mapToSecurities(security)
              );
            }
            return [];
          }
        )
      );
  }

  updateSecurity(
    securityId: number,
    securityRequest: UpdateSecurityRequest
  ): Observable<void> {
    return this.gstarClient.securitiesPUT(securityId, securityRequest);
  }

  reviewWithAnswers(
    query?: Partial<QueryOptions<Security>>
  ): Observable<SecurityReview[]> {
    return this.gstarClient
      .reviewwithanswers(
        query?.select?.toString(),
        buildQueryFilter(query),
        query?.top?.toString(),
        query?.skip?.toString(),
        query?.orderBy?.toString(),
        query?.count?.toString(),
        undefined
      )
      .pipe(
        map(
          (
            response: SecurityReviewResponseWithAnswersAndNestedObjectsODataWithMetadataResponse
          ) => this.mapToSecurityReview(response.value)
        )
      );
  }

  approve(
    approveSecurityRequest: ApproveSecurityRequest[]
  ): Observable<SecurityReviewResult[]> {
    return this.gstarClient.approveAll(approveSecurityRequest).pipe(
      map((securitiesActionResult: SecurityActionResult[]) => {
        return Mapper.mapByList(securitiesActionResult, SecurityReviewResult, [
          'message',
          'securityId',
          'status',
        ]);
      })
    );
  }

  changeToSpecificAdvice(
    securityId: number,
    securityRequest: UpdateSecurityTransactionTypeRequest
  ): Observable<void> {
    return this.gstarClient.transactiontype(securityId, securityRequest);
  }

  private mapToSecurityReview(
    securitiesReview:
      | SecurityReviewResponseWithAnswersAndNestedObjects[]
      | undefined
  ): SecurityReview[] {
    if (securitiesReview) {
      let securitiesQuestionAnswerList: SecurityQuestionAnswer[];

      return Mapper.mapByList(securitiesReview, SecurityReview, [
        'securityId',
        'generalRateEntityTypeId',
        'name',
        'isinCusip',
        'description',
        'acquisitionDate',
        'dispositionDate',
        'isActive',
        'created',
        'createdBy',
        'modified',
        'modifiedBy',
        {
          generalRateEntityType: source =>
            source.generalRateEntityType
              ? Mapper.map(source.generalRateEntityType, EntityType, [
                  'entityTypeId',
                  'name',
                ])
              : undefined,
          entity: source =>
            source.entity
              ? Mapper.map(source.entity, Entity, [
                  'entityId',
                  'description',
                  'name',
                  'isActive',
                  {
                    entityType: source =>
                      Mapper.map(source.entityType, EntityType, [
                        'name',
                        'entityTypeId',
                        {
                          country: source =>
                            source && !source.country
                              ? null
                              : Mapper.map(source?.country, Country, [
                                  'name',
                                  'code',
                                  'countryId',
                                ]),
                        },
                      ]),
                    entityTypeId: source => source.entityType?.entityTypeId,
                    client: source =>
                      !source.client
                        ? null
                        : Mapper.map(source.client, Client, [
                            'name',
                            'clientId',
                          ]),
                    clientId: source => source.client?.clientId,
                    residencyJurisdiction: source =>
                      Mapper.map(
                        source.residencyJurisdiction,
                        ResidencyJurisdiction,
                        [
                          'residencyJurisdictionId',
                          'modified',
                          {
                            countryId: source => source?.country?.countryId,
                            isActive: source => source?.country?.isActive,
                            country: source =>
                              source && !source.country
                                ? null
                                : Mapper.map(source?.country, Country, [
                                    'name',
                                    'code',
                                    'countryId',
                                    {
                                      region: source => source?.region?.name,
                                    },
                                  ]),
                            name: source => source?.country?.name,
                            region: source =>
                              source?.country?.region
                                ? source?.country?.region.name
                                : '',
                          },
                        ]
                      ),
                    residencyJurisdictionId: source =>
                      source.residencyJurisdiction?.residencyJurisdictionId,
                  },
                ])
              : null,
          securityStatus: (
            source: SecurityReviewResponseWithAnswersAndNestedObjects
          ): SecurityStatus => {
            return Mapper.map(source.securityStatus, SecurityStatus, [
              'securityStatusId',
              'name',
            ]);
          },
          securityQuestionAnswers: (
            source: SecurityReviewResponseWithAnswersAndNestedObjects
          ): SecurityQuestionAnswer[] => {
            securitiesQuestionAnswerList = Mapper.mapByList(
              source.securityQuestionAnswers ?? [],
              SecurityQuestionAnswer,
              [
                'questionId',
                'isActive',
                'comment',
                'taxRuleId',
                {
                  text: (
                    source: SecurityQuestionAnswerAuditableResponse
                  ): boolean => source.text === BooleanString.TRUE,
                  question: (
                    source: SecurityQuestionAnswerAuditableResponse
                  ): Question | null =>
                    !source.question
                      ? null
                      : Mapper.map(source.question, Question, [
                          'name',
                          'questionId',
                          'text',
                          'isActive',
                        ]),
                  taxPolicy: (
                    source: SecurityQuestionAnswerAuditableResponse
                  ): TaxPolicy | null =>
                    Mapper.map(source.taxPolicy, TaxPolicy, [
                      'taxPolicyId',
                      'name',
                      'isActive',
                      {
                        taxResultItemType: (
                          source: TaxPolicyForSecurityReviewResponse | undefined
                        ): TaxResultItemType | null =>
                          Mapper.map(
                            source?.taxResultItemType,
                            TaxResultItemType,
                            ['name', 'taxResultItemTypeId']
                          ),
                      },
                    ]),
                },
                'unsureAnswer',
              ]
            );

            return securitiesQuestionAnswerList;
          },
          securityTaxResultItems: source =>
            Mapper.mapByList(
              source.securityTaxResultItems ?? [],
              SecurityTaxResultItem,
              [
                'isActive',
                'securityTaxResultItemId',
                'taxPolicyId',
                'taxRuleId',
                {
                  taxResultItemType: (
                    source: SecurityTaxResultItemNestedObjectResponse
                  ) =>
                    Mapper.map(source.taxResultItemType, TaxResultItemType, [
                      'taxResultItemTypeId',
                      'name',
                    ]),
                  taxResultItemTypeId: source =>
                    source.taxResultItemType?.taxResultItemTypeId,
                  taxResultItemId: source =>
                    source.taxResultItem?.taxResultItemId,
                  securityTaxResultItemRate: (
                    source
                  ): number | null | undefined => source.rate,
                  taxResultItemRate: (source): number | null | undefined =>
                    source.taxResultItem?.rate,
                  securityTaxResultItemDescription: (
                    source
                  ): string | null | undefined => source.description,
                  taxResultItemDescription: (
                    source
                  ): string | null | undefined =>
                    source.taxResultItem?.description,
                },
              ]
            ),
          transactionType: source =>
            Mapper.map(source.transactionType, TransactionType, [
              'transactionTypeId',
              'description',
              'name',
            ]),
          securityType: source =>
            Mapper.map(source.securityType, SecurityType, [
              'name',
              'securityTypeId',
            ]),
          investmentJurisdiction: source =>
            Mapper.map(source.investmentJurisdiction, InvestmentJurisdiction, [
              'investmentJurisdictionId',
              {
                name: source => source?.country?.name,
                country: source =>
                  source && !source.country
                    ? null
                    : Mapper.map(source?.country, Country, [
                        'name',
                        'code',
                        'countryId',
                        'isActive',
                        {
                          region: source => source?.region?.name,
                        },
                      ]),
              },
            ]),
          comment: source => source.comment ?? '',
          adminComment: source =>
            ((source as unknown as Record<string, unknown>)['adminComment'] as
              | string
              | undefined) ?? '',
          adminCommentModifiedBy: source =>
            ((source as unknown as Record<string, unknown>)[
              'adminCommentModifiedBy'
            ] as string | undefined) ?? '',
          adminCommentModifiedDate: (
            source: SecurityReviewResponseWithAnswersAndNestedObjectsODataWithMetadataResponse
          ): Date | undefined => {
            const raw = (source as unknown as Record<string, unknown>)[
              'adminCommentModifiedDate'
            ] as string | undefined;

            return raw ? new Date(raw) : undefined;
          },
        },
      ]);
    }
    return [];
  }

  private mapToSecurities(
    security: SecurityByIdResponseWithNestedObjectsAuditable
  ): Security {
    return Mapper.map(security, Security, [
      'securityId',
      'description',
      'name',
      'isActive',
      'acquisitionDate',
      'dispositionDate',
      'modified',
      'comment',
      {
        adminComment: source =>
          ((source as unknown as Record<string, unknown>)['adminComment'] as
            | string
            | undefined) ?? '',
      },
      {
        adminCommentModifiedBy: source =>
          ((source as unknown as Record<string, unknown>)[
            'adminCommentModifiedBy'
          ] as string | undefined) ?? '',
      },
      {
        adminCommentModifiedDate: (
          source: SecurityReviewResponseWithAnswersAndNestedObjectsODataWithMetadataResponse
        ): Date | undefined => {
          const raw = (source as unknown as Record<string, unknown>)[
            'adminCommentModifiedDate'
          ] as string | undefined;

          return raw ? new Date(raw) : undefined;
        },
      },
      {
        entity: (source: SecurityResponseWithNestedObjectsAuditable) =>
          source.entity
            ? Mapper.map(source.entity, Entity, [
                'entityId',
                'clientId',
                {
                  client: source =>
                    Mapper.map(source.client, Client, ['clientId', 'name']),
                },
                'residencyJurisdictionId',
                'residencyJurisdiction',
                'entityTypeId',
                'description',
                'name',
                'isActive',
                {
                  entityType: source =>
                    Mapper.map(source.entityType, EntityType, [
                      'name',
                      'entityTypeId',
                    ]),
                },
              ])
            : null,
        securityStatus: (source: SecurityResponseWithNestedObjectsAuditable) =>
          Mapper.map(source.securityStatus, SecurityStatus, [
            'securityStatusId',
            'name',
          ]),
        securityTaxResultItems: (
          source: SecurityResponseWithNestedObjectsAuditable
        ) =>
          Mapper.mapByList(
            source.securityTaxResultItems ?? [],
            SecurityTaxResultItem,
            [
              'description',
              'isActive',
              'rate',
              'securityTaxResultItemId',
              'taxPolicyId',
              'taxResultItemId',
              'taxRuleId',
              {
                taxResultItemType: (
                  source: SecurityTaxResultItemNestedObjectResponse
                ) =>
                  Mapper.map(source.taxResultItemType, TaxResultItemType, [
                    'taxResultItemTypeId',
                    'name',
                  ]),
              },
            ]
          ),
        transactionType: (source: SecurityResponseWithNestedObjectsAuditable) =>
          Mapper.map(source.transactionType, TransactionType, [
            'transactionTypeId',
            'description',
            'name',
          ]),
        securityType: (source: SecurityResponseWithNestedObjectsAuditable) =>
          Mapper.map(source.securityType, SecurityType, [
            'description',
            'name',
            'securityTypeId',
          ]),
        investmentJurisdiction: source =>
          Mapper.map(source.investmentJurisdiction, InvestmentJurisdiction, [
            'investmentJurisdictionId',
            {
              country: source =>
                source && !source.country
                  ? null
                  : Mapper.map(source?.country, Country, [
                      'name',
                      'code',
                      'countryId',
                      'isActive',
                    ]),
            },
          ]),
      },
    ]);
  }
}
